import { Avatar, Button, Card, Flex, List, Progress, theme } from 'antd';
import { LuCheck, LuChevronRight, LuLock, LuPlus } from 'react-icons/lu';

import BaseText from '@components/typography/BaseText';

import { TRAINING_GATE } from '../../constants';
import { isSystemNode } from '../../objectTree';
import { formatCount } from './objectsUtils';

const getTitle = (object, isReady) => {
	if (isReady) return `Train new object from ${object?.name ?? ''}`;
	return isSystemNode(object)
		? 'Need more annotations to train'
		: 'Need more annotations to re-train';
};

/**
 * Card that tells the user whether a child object can be trained from the
 * selected node, and what to do if not. The two states come from
 * `getTrainingGate`:
 *   - ready:  progress complete, "Train Object" starts the create form
 *   - locked: progress short, numbered unlock steps and "Select project"
 *
 * @param {object} object - the selected taxonomy node
 * @param {{ status: string, annotationCount: number, target: number, missing: number, percent: number }} gate
 * @param {() => void} onTrain - "Train Object" pressed (ready state only)
 * @param {() => void} onSelectProject - "Select project" pressed (locked state only)
 */
const TrainingDetailCard = ({ object, gate, onTrain, onSelectProject }) => {
	const { token } = theme.useToken();

	const { status, annotationCount, target, missing, percent } = gate;
	const isReady = status === TRAINING_GATE.READY;
	const accent = isReady ? token.colorPrimary : token.colorWarning;
	const Icon = isReady ? LuCheck : LuLock;

	const unlockSteps = [
		'Open a project.',
		`Annotate ${formatCount(missing)} more “${object?.name ?? ''}”.`,
		'Create from there, or return here.',
	];

	return (
		<Card
			size="small"
			style={{ borderColor: isReady ? token.colorPrimaryBorder : token.colorWarningBorder }}>
			<Flex vertical gap={12}>
				<Flex align="center" gap={8}>
					<Avatar
						size={24}
						icon={<Icon size={13} aria-hidden="true" />}
						style={{ backgroundColor: accent, flex: 'none' }}
					/>
					<BaseText strong size="sm" type={isReady ? 'success' : 'warning'}>
						{getTitle(object, isReady)}
					</BaseText>
				</Flex>

				<div>
					<Progress
						percent={percent}
						showInfo={false}
						size="small"
						strokeColor={accent}
						aria-label={`${formatCount(annotationCount)} of ${formatCount(target)} annotations`}
					/>
					<Flex justify="space-between">
						<BaseText type="secondary" size="xs">
							{formatCount(annotationCount)} annotated
						</BaseText>
						<BaseText type="secondary" size="xs">
							{formatCount(target)} needed
						</BaseText>
					</Flex>
				</div>

				{isReady && (
					<Button type="primary" icon={<LuPlus aria-hidden="true" />} onClick={onTrain}>
						Train Object
					</Button>
				)}

				{!isReady && (
					<Flex vertical gap={8} align="flex-start">
						<List
							size="small"
							split={false}
							dataSource={unlockSteps}
							renderItem={(step, index) => (
								<List.Item>
									<Flex align="flex-start" gap={8}>
										<Avatar size={18}>
											<BaseText size="xxs">{index + 1}</BaseText>
										</Avatar>
										<BaseText type="secondary" size="xs">
											{step}
										</BaseText>
									</Flex>
								</List.Item>
							)}
						/>
						<Button
							icon={<LuChevronRight aria-hidden="true" />}
							iconPlacement="end"
							onClick={onSelectProject}>
							Select project
						</Button>
					</Flex>
				)}
			</Flex>
		</Card>
	);
};

export default TrainingDetailCard;
